<template>
  <div class="card card-h-100">
    <div class="card-body">
      <h4 v-if="title" class="header-title mb-3">{{ title }}</h4>

      <apexchart
        height="270"
        type="area"
        :class="{ 'apex-charts': true, 'pacsort-tooltip-chart': true, 'mt-3': !!title }"
        :series="series"
        :options="chartOptions"
      ></apexchart>
    </div>
  </div>
</template>

<script lang="ts">
import { buildChartTooltip } from '@/utils/chart-tooltip'
import type { Periods } from '@/typings/Periods'
import { computed, defineComponent, type PropType, ref, watch } from 'vue'
import { differenceInDays } from 'date-fns'
import moment from 'moment'

export default defineComponent({
  props: {
    periodData: {
      type: Object as PropType<Periods>,
      required: true,
    },
    title: {
      type: String,
      required: false,
      default: '',
    },
    unit: {
      type: String,
      required: false,
      default: '',
    },
  },
  setup(props) {
    const getBaseSeries = () => [
      {
        name: 'Selected Period',
        data: props.periodData.currentPeriod,
      },
      {
        name: 'Previous Period',
        data: props.periodData.previousPeriod,
      },
    ]

    const startDate = computed(() => new Date(props.periodData.periodLabels[0]))
    const endDate = computed(() => new Date(props.periodData.periodLabels[props.periodData.periodLabels.length - 1]))
    const shift = computed(() => {
      const daysDiff = Math.round(differenceInDays(endDate.value, startDate.value))
      const moduloShift = 7 - (daysDiff % 7)
      return daysDiff + moduloShift
    })

    const getBaseOptions = () => ({
      chart: {
        fontFamily: 'PacSort TT Norms, Arial, sans-serif',
        foreColor: '#6c8071',
        animations: { enabled: false },
        toolbar: {
          show: false,
        },
      },
      dataLabels: {
        enabled: false,
      },
      stroke: {
        curve: 'straight',
        dashArray: [0, 5],
        width: 2,
      },
      zoom: {
        enabled: false,
      },
      legend: {
        show: true,
      },
      tooltip: {
        shared: true,
        intersect: false,
        custom: ({ dataPointIndex }: { dataPointIndex: number }) => {
          const date = moment(props.periodData.periodLabels[dataPointIndex])
          return buildChartTooltip(props.title, date.format('D MMM YYYY'), props.unit, [
            { name: 'Selected period', value: props.periodData.currentPeriod[dataPointIndex], color: '#147c73' },
            { name: `Previous period (${date.clone().subtract(shift.value, 'days').format('D MMM YYYY')})`, value: props.periodData.previousPeriod[dataPointIndex], color: '#b4cdbd' },
          ], false)
        },
      },
      colors: ['#147c73', '#b4cdbd'],
      xaxis: {
        type: 'category',
        tickAmount: 5,
        categories: props.periodData.periodLabels.map((value: string) =>
          moment(value, 'YYYY-MM-DD', true).isValid() ? moment(value).format('D MMM') : value,
        ),
        tooltip: {
          enabled: false,
        },
        axisBorder: {
          show: false,
        },
        labels: {
          formatter: (val: string) => val,
        },
      },
      yaxis: {
        labels: {
          formatter: (val: number) =>
            props.unit ? `${(Math.round(val * 100) / 100).toFixed(2) + ' ' + props.unit}` : val,
          offsetX: -15,
        },
      },
      fill: {
        type: 'gradient',
        gradient: {
          type: 'vertical',
          shadeIntensity: 1,
          inverseColors: false,
          opacityFrom: 0.45,
          opacityTo: 0.05,
          stops: [45, 100],
        },
      },
    })

    const series = ref(getBaseSeries())
    const chartOptions = ref(getBaseOptions())

    watch(
      () => props.periodData,
      () => {
        series.value = getBaseSeries()
        chartOptions.value = getBaseOptions()
      },
      { deep: true },
    )

    return {
      series,
      chartOptions,
    }
  },
})
</script>
